import { useEffect, useState, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stack,
  Loader,
  Alert,
  Paper,
  Text,
  Group,
  Title,
  Anchor,
  Badge,
  EmptyState,
  VIPSO_NAVY,
  VIPSO_RED,
  formatRupee,
} from '@vipso/shared';
import {
  IconUsers,
  IconBuildingStore,
  IconPackage,
  IconShoppingCart,
  IconUserCheck,
  IconBuildingWarehouse,
  IconTruck,
  IconHammer,
  IconChecklist,
} from '@tabler/icons-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import {
  useDashboardStatsQuery,
  useListProcurementQuery,
  type ProcRow,
} from '../api/tenantApi';
import type { RootState } from '../store';
import { useModuleLabel } from '../hooks/useModuleLabel';
import { useNavAccess } from '../hooks/useNavAccess';

const PIE_COLORS = [VIPSO_RED, VIPSO_NAVY];

type DashTile = {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number | string; stroke?: number; color?: string }>;
};

function useIsNarrow(breakpoint = 960) {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  );
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return narrow;
}

function RecentProcRow({ row, type }: { row: ProcRow; type: 'SALES' | 'PURCHASE' }) {
  const { t } = useTranslation();
  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      p="sm"
      style={{
        borderRadius: 10,
        background: 'var(--mantine-color-gray-0)',
        border: '1px solid var(--mantine-color-gray-2)',
      }}
    >
      <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
        <Text size="sm" fw={600} truncate>
          {row.ordNumber || t('dashboard.orderFallback', { id: row.id.slice(0, 8) })}
        </Text>
        <Text size="xs" c="dimmed" truncate>
          {row.customerId || row.vendorId
            ? t('dashboard.refPrefix', {
                id: (row.customerId || row.vendorId || '').slice(0, 8),
              })
            : type === 'SALES'
              ? t('orders.salesOrder')
              : t('orders.purchaseOrder')}
        </Text>
      </Stack>
      <Stack gap={4} align="flex-end">
        <Badge size="sm" variant="light" color={type === 'SALES' ? 'green' : 'red'}>
          {type === 'SALES' ? t('orders.sales') : t('orders.purchase')}
        </Badge>
        <Text size="sm" fw={700} c="brand.7">
          {formatRupee(row.procAmount)}
        </Text>
      </Stack>
    </Group>
  );
}

function ModuleTile({
  tile,
  isMobile,
  onOpen,
}: {
  tile: DashTile;
  isMobile: boolean;
  onOpen: () => void;
}) {
  const IconCmp = tile.icon;
  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      style={{
        flex: '0 0 auto',
        width: isMobile ? 'calc(100vw - 48px)' : 200,
        minHeight: isMobile ? 140 : 120,
        scrollSnapAlign: 'start',
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 14,
        background: '#fff',
        padding: 20,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        textAlign: 'center',
        boxShadow: '0 2px 10px rgba(0,14,33,0.04)',
        transition: 'transform 140ms ease, box-shadow 140ms ease',
      }}
    >
      <Box
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'var(--mantine-color-brand-0)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <IconCmp size={26} stroke={1.75} color={VIPSO_RED} />
      </Box>
      <Text size="sm" fw={600} c="navy.9">
        {tile.label}
      </Text>
    </Box>
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsNarrow();
  const org = useSelector((state: RootState) => state.auth.org);
  const user = useSelector((state: RootState) => state.auth.user);
  const pendingTaskCount = useSelector((state: RootState) => state.auth.pendingTaskCount);
  const customersLabel = useModuleLabel('customer');
  const vendorsLabel = useModuleLabel('vendor');
  const productsLabel = useModuleLabel('product');
  const nav = useNavAccess();
  const dashboardTiles: DashTile[] = [
    ...(nav.customers
      ? [{ to: '/customers', label: customersLabel, icon: IconUsers }]
      : []),
    ...(nav.vendors
      ? [{ to: '/vendors', label: vendorsLabel, icon: IconBuildingStore }]
      : []),
    ...(nav.products
      ? [{ to: '/products', label: productsLabel, icon: IconPackage }]
      : []),
    ...(nav.orders
      ? [{ to: '/orders', label: t('nav.orders'), icon: IconShoppingCart }]
      : []),
    ...(nav.staff
      ? [{ to: '/staff', label: t('nav.staff'), icon: IconUserCheck }]
      : []),
    ...(nav.warehouse
      ? [{ to: '/warehouse', label: t('nav.warehouse'), icon: IconBuildingWarehouse }]
      : []),
    ...(nav.transport
      ? [{ to: '/transport', label: t('nav.transport'), icon: IconTruck }]
      : []),
    ...(nav.labour
      ? [{ to: '/labour', label: t('nav.labour'), icon: IconHammer }]
      : []),
    ...(nav.tasks
      ? [{ to: '/tasks', label: t('nav.tasks'), icon: IconChecklist }]
      : []),
  ];

  const { data, isLoading, isError } = useDashboardStatsQuery(undefined, {
    skip: !org,
  });

  const { data: salesData, isLoading: salesLoading } = useListProcurementQuery(
    { listType: 'SALES', page: 1, pageLimit: 5 },
    { skip: !org },
  );
  const { data: purchaseData, isLoading: purchaseLoading } = useListProcurementQuery(
    { listType: 'PURCHASE', page: 1, pageLimit: 5 },
    { skip: !org },
  );

  const recentSales = salesData?.listProcurement?.rows ?? [];
  const recentPurchases = purchaseData?.listProcurement?.rows ?? [];

  const salesLabel = t('orders.sales');
  const purchasesLabel = t('orders.purchase');

  const barData = [
    { name: customersLabel, value: data?.customers ?? 0 },
    { name: vendorsLabel, value: data?.vendors ?? 0 },
    { name: productsLabel, value: data?.products ?? 0 },
    { name: salesLabel, value: data?.salesOrders ?? 0 },
    { name: purchasesLabel, value: data?.purchaseOrders ?? 0 },
  ];

  const pieData = [
    { name: salesLabel, value: data?.salesOrders ?? 0 },
    { name: purchasesLabel, value: data?.purchaseOrders ?? 0 },
  ].filter((d) => d.value > 0);

  if (isLoading && !data) {
    return (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {!isMobile ? (
        <Paper
          p="lg"
          radius="md"
          style={{
            border: '1px solid var(--mantine-color-gray-3)',
            background:
              'linear-gradient(120deg, var(--mantine-color-brand-0) 0%, #ffffff 48%, rgba(0,14,33,0.04) 100%)',
          }}
        >
          <Stack gap={4}>
            <Text size="sm" c="dimmed" fw={600} tt="uppercase">
              {t('dashboard.title')}
            </Text>
            <Title order={2} c="navy.9">
              {org?.name || t('dashboard.workspace')}
            </Title>
            <Text size="sm" c="dimmed">
              {t('dashboard.welcome', { name: user?.name ? `, ${user.name}` : '' })}
            </Text>
          </Stack>
        </Paper>
      ) : null}

      {pendingTaskCount > 0 ? (
        <Alert color="orange" title={t('dashboard.pendingTasksTitle', { count: pendingTaskCount })}>
          <Group justify="space-between" align="center" wrap="wrap">
            <Text size="sm">{t('dashboard.pendingTasksMessage')}</Text>
            <Anchor
              component="button"
              type="button"
              size="sm"
              fw={600}
              onClick={() => navigate('/tasks')}
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              {t('dashboard.openTasks')}
            </Anchor>
          </Group>
        </Alert>
      ) : null}

      {isError ? (
        <Alert color="yellow" title={t('dashboard.limitedStatsTitle')}>
          {t('dashboard.statsFailed')}
        </Alert>
      ) : null}

      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Title order={5} c="navy.9">
            {t('dashboard.recentSales')}
          </Title>
          <Anchor
            component="button"
            type="button"
            size="sm"
            c="brand.7"
            fw={600}
            onClick={() => navigate('/orders?type=SALES')}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {t('common.viewAll')}
          </Anchor>
        </Group>
        {salesLoading && recentSales.length === 0 ? (
          <Loader size="sm" />
        ) : recentSales.length === 0 ? (
          <Paper p="md" radius="md" style={{ border: '1px solid var(--mantine-color-gray-3)' }}>
            <EmptyState
              title={t('dashboard.noRecentSales')}
              description={t('dashboard.noRecentSalesDesc')}
            />
          </Paper>
        ) : (
          <Stack gap="xs">
            {recentSales.map((row) => (
              <RecentProcRow key={row.id} row={row} type="SALES" />
            ))}
          </Stack>
        )}
      </Stack>

      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Title order={5} c="navy.9">
            {t('dashboard.recentPurchases')}
          </Title>
          <Anchor
            component="button"
            type="button"
            size="sm"
            c="brand.7"
            fw={600}
            onClick={() => navigate('/orders?type=PURCHASE')}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {t('common.viewAll')}
          </Anchor>
        </Group>
        {purchaseLoading && recentPurchases.length === 0 ? (
          <Loader size="sm" />
        ) : recentPurchases.length === 0 ? (
          <Paper p="md" radius="md" style={{ border: '1px solid var(--mantine-color-gray-3)' }}>
            <EmptyState
              title={t('dashboard.noRecentPurchases')}
              description={t('dashboard.noRecentPurchasesDesc')}
            />
          </Paper>
        ) : (
          <Stack gap="xs">
            {recentPurchases.map((row) => (
              <RecentProcRow key={row.id} row={row} type="PURCHASE" />
            ))}
          </Stack>
        )}
      </Stack>

      <Stack gap="sm">
        <Title order={5} c="navy.9">
          {t('dashboard.overview')}
        </Title>
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1.4fr 1fr',
            gap: 16,
          }}
        >
          <Paper
            p="md"
            radius="md"
            style={{ border: '1px solid var(--mantine-color-gray-3)', background: '#fff' }}
          >
            <Text size="sm" fw={600} mb="sm" c="navy.8">
              {t('dashboard.countsByModule')}
            </Text>
            <Box style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--mantine-color-gray-3)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <RechartsTooltip />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {barData.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={
                          entry.name === salesLabel || entry.name === purchasesLabel
                            ? VIPSO_RED
                            : VIPSO_NAVY
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          <Paper
            p="md"
            radius="md"
            style={{ border: '1px solid var(--mantine-color-gray-3)', background: '#fff' }}
          >
            <Text size="sm" fw={600} mb="sm" c="navy.8">
              {t('dashboard.salesVsPurchases')}
            </Text>
            <Box style={{ width: '100%', height: 220 }}>
              {pieData.length === 0 ? (
                <Stack align="center" justify="center" style={{ height: '100%' }}>
                  <Text size="sm" c="dimmed">
                    {t('dashboard.noOrderData')}
                  </Text>
                </Stack>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={48}
                      outerRadius={78}
                      paddingAngle={3}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend />
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Box>
      </Stack>
    </Stack>
  );
}
